import {
    IonButton,
    IonButtons,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonMenu,
    IonMenuToggle,
    IonNote,
    IonToolbar,
} from '@ionic/react';

import React, {MenuHTMLAttributes, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useAuthStore} from '../../redux/auth';

import {
    fileTrayFull,
    fileTrayFullOutline,
    folder,
    folderOutline,
    home,
    homeOutline,
    informationCircle,
    informationCircleOutline,
    logOut,
    logOutOutline,
    notifications,
    notificationsOutline,
    receipt,
    receiptOutline,
    shieldCheckmark,
    shieldCheckmarkOutline,
    swapHorizontal,
    swapHorizontalOutline,
    warningOutline,
    warningSharp,
    settings,
    settingsOutline,
    chatbubbles,
    chatbubblesOutline, cart, cartOutline,
} from 'ionicons/icons';


import './Menu.css';
import {useCustomerStore} from "../../redux/customers";
import {useModalPageStore} from "../../redux/modal-pages";
import {useUserStore} from "../../redux/users";

interface AppPage {
    url?: string;
    routerLink?: string;
    onClick?: any;
    href?: any;
    visible?: boolean;
    iosIcon: string;
    mdIcon: string;
    title: string;
}

interface InfoPage {
    url?: string;
    setIsOpen: (isOpen: boolean) => void;
    visible?: boolean;
    iosIcon: string;
    mdIcon: string;
    title: string;
}

interface MenuProps {
    setMustLogout: (newMustLogout: boolean) => void
}

const Menu: React.FC<MenuProps> = (props) => {
    const menuRef = useRef<MenuHTMLAttributes<any>>();
    const location = useLocation();
    const history = useHistory();
    const authStore = useAuthStore();
    const {userName, isAuthed} = authStore;
    const userStore = useUserStore();
    const {setIsOpenDeleteModal} = userStore;
    const customerStore = useCustomerStore();
    const {customersCount} = customerStore;
    const hasMultipleCustomers = (customersCount > 1);
    const {activeCustomer} = customerStore;
    const {setMustLogout} = props;
    const readOnly = !!activeCustomer?.accountReadOnly;

    const modalPageStore = useModalPageStore();
    const {setIsOpenAbout, setIsOpenPrivacy, setIsOpenTermsOfUse} = modalPageStore;

    // The username taps through to Settings — but only when that surface is
    // actually available (same feature gate the Settings menu item uses).
    // When it isn't, there's no handler so the tap is inert (no dead nav).
    const canOpenSettings = isAuthed && !!activeCustomer && (
        !!activeCustomer?.company.featuresSelfManage
        || !!activeCustomer?.company.featuresMarketing
    );

    const infoPages: InfoPage[] = [
        {
            title: 'Delete Account',
            setIsOpen: setIsOpenDeleteModal,
            iosIcon: warningOutline,
            mdIcon: warningSharp
        },
        {
            title: 'App info',
            setIsOpen: setIsOpenAbout,
            iosIcon: informationCircleOutline,
            mdIcon: informationCircle
        },
        {
            title: 'Privacy Policy',
            setIsOpen: setIsOpenPrivacy,
            iosIcon: shieldCheckmarkOutline,
            mdIcon: shieldCheckmark
        },
        {
            title: 'Terms of Use',
            setIsOpen: setIsOpenTermsOfUse,
            iosIcon: folderOutline,
            mdIcon: folder
        },
    ]

    const appPages: AppPage[] = [
        {
            title: 'Home',
            routerLink: '/home',
            iosIcon: homeOutline,
            mdIcon: home,
            visible: isAuthed && !!activeCustomer
        },
        {
            title: 'Account',
            routerLink: '/account',
            iosIcon: fileTrayFullOutline,
            mdIcon: fileTrayFull,
            visible: isAuthed && !!activeCustomer && !!activeCustomer?.company.useBilling && !!activeCustomer?.company.featuresAppFinancials
        },
        {
            title: 'Shop',
            routerLink: '/my-shop',
            iosIcon: cartOutline,
            mdIcon: cart,
            visible: isAuthed && !!activeCustomer && !!activeCustomer?.company.featuresShop && !readOnly
        },
        {
            title: 'My Orders',
            routerLink: '/orders',
            iosIcon: receiptOutline,
            mdIcon: receipt,
            visible: isAuthed && !!activeCustomer && !!activeCustomer?.company.featuresShop && !readOnly
        },
        {
            title: 'Chat',
            routerLink: '/chat',
            iosIcon: chatbubblesOutline,
            mdIcon: chatbubbles,
            visible: isAuthed && !!activeCustomer && !!activeCustomer?.company.featuresChat && !readOnly
        },
        {
            title: 'Communications',
            routerLink: '/notifications',
            iosIcon: notificationsOutline,
            mdIcon: notifications,
            visible: isAuthed && !!activeCustomer && !readOnly
        },
        {
            title: 'Switch Account',
            routerLink: '/customer-select',
            iosIcon: swapHorizontalOutline,
            mdIcon: swapHorizontal,
            visible: isAuthed && !!hasMultipleCustomers,
        },

        {
            title: 'Settings',
            routerLink: '/settings',
            iosIcon: settingsOutline,
            mdIcon: settings,
            visible: canOpenSettings
        },
        {
            title: 'Log Out',
            onClick: () => {
                setMustLogout(true);
            },
            iosIcon: logOutOutline,
            mdIcon: logOut,
            visible: isAuthed
        }
    ];

    return (
        <IonMenu contentId="main" side="start" type="reveal" ref={() => menuRef}>
            <IonContent>
                <IonList>
                    <IonListHeader>{(activeCustomer?.company && activeCustomer.company.name) || 'swool.io'}</IonListHeader>
                    <IonMenuToggle>
                        <IonNote onClick={canOpenSettings ? () => history.push('/settings') : undefined}>
                            {userName}
                        </IonNote>
                    </IonMenuToggle>
                    {appPages.map((appPage, index) => {
                        return (
                            appPage.visible !== false &&
                            (
                                <IonMenuToggle key={index} autoHide={false}>
                                    <IonItem button className={location.pathname === appPage.routerLink ? 'selected' : ''}
                                             href={appPage.href}
                                             routerLink={appPage.routerLink} onClick={appPage.onClick}
                                             routerDirection="none" lines="none" detail={false}
                                    >
                                        <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon}/>
                                        <IonLabel>{appPage.title}</IonLabel>
                                    </IonItem>
                                </IonMenuToggle>
                            )
                        );
                    })}
                </IonList>
            </IonContent>
            <IonFooter>
                <IonToolbar className="menu-footer-toolbar">
                    <IonButtons slot={"start"} className={''}>
                        {infoPages.map((infoPage, index) => {
                            return (
                                infoPage.visible !== false &&
                                (
                                    <IonMenuToggle key={index} autoHide={false}>
                                        <IonButton
                                            className={''}
                                            color={"medium"}
                                            size={"small"}
                                            onClick={() => infoPage.setIsOpen(true)}
                                        >
                                            <div className="menu-footer-button-inner">
                                                <IonIcon
                                                    size={"small"}
                                                    slot="start"
                                                    ios={infoPage.iosIcon}
                                                    md={infoPage.mdIcon}
                                                    style={{marginBottom: "-4px"}}
                                                />
                                                <IonLabel className="menu-footer-button-inner">{infoPage.title}</IonLabel>
                                            </div>
                                        </IonButton>
                                    </IonMenuToggle>
                                )
                            );
                        })}
                    </IonButtons>
                </IonToolbar>
            </IonFooter>
        </IonMenu>
    );
};

export default Menu;



